// The Structure tab's API calls, all in one module: the structure and the AI preferences (P4-A01,
// structure/router.py), the website's `structure_mode` and `variant_count` (PATCH /admin/websites/{id}), the
// gallery's presets, and AI mode's suggestion (P4-A02).
import { infiniteQueryOptions, queryOptions, type QueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/api/client';
import { waitForJob } from '@/api/jobs';
import { qk } from '@/api/queries';
import type { PresetOut, PresetPage, Website } from '@/api/types';

export type StructureOut = Schemas['StructureOut'];
export type SectionOut = Schemas['SectionOut'];
export type SectionIn = Schemas['SectionIn'];
export type PinnedPreset = Schemas['PinnedPreset'];
export type Preferences = Schemas['PreferencesIn'];
export type StructureMode = Schemas['StructureMode'];

export const structureKey = (siteId: string) => ['websites', 'one', siteId, 'structure'] as const;
export const preferencesKey = (siteId: string) => ['websites', 'one', siteId, 'preferences'] as const;

/** GET /admin/websites/{id}/structure: the ordered sections with their pinned preset versions. */
export const structureQuery = (siteId: string) =>
  queryOptions({
    queryKey: structureKey(siteId),
    queryFn: (): Promise<StructureOut> => unwrap(api.GET('/admin/websites/{website_id}/structure', { params: { path: { website_id: siteId } } })),
  });

/** PUT /admin/websites/{id}/structure: the whole ordered list (order = position); 0–N versions per section. */
export function saveStructure(siteId: string, sections: SectionIn[]): Promise<StructureOut> {
  return unwrap(api.PUT('/admin/websites/{website_id}/structure', { params: { path: { website_id: siteId } }, body: { sections } }));
}

/** GET /admin/websites/{id}/preferences: «Предпочтения для AI». */
export const preferencesQuery = (siteId: string) =>
  queryOptions({
    queryKey: preferencesKey(siteId),
    queryFn: () => unwrap(api.GET('/admin/websites/{website_id}/preferences', { params: { path: { website_id: siteId } } })),
  });

/** PUT /admin/websites/{id}/preferences: saved per site, automatically (the drawer has no save button). */
export function savePreferences(siteId: string, body: Preferences): Promise<Schemas['PreferencesOut']> {
  return unwrap(api.PUT('/admin/websites/{website_id}/preferences', { params: { path: { website_id: siteId } }, body }));
}

// ── The gallery: the library's presets of one section type (P3-D01 GET /admin/presets) ──

export type GalleryQuery = {
  section_type: string;
  layout?: string;
  color_mode?: 'light' | 'soft' | 'inv' | 'acc';
  favorites?: boolean;
  search?: string;
};

/** Cards per server page (the gallery asks for the next one as the PM scrolls to the end). */
export const GALLERY_PAGE = 100;

/**
 * Every origin, in library order (`sort=position`: the design's parametric presets first, then the
 * authored and catalog ones); a PM gets published presets only. The page's `facets` count the type's
 * presets per layout, whatever the other filters (the layout chips).
 */
export const galleryQuery = (f: GalleryQuery) =>
  infiniteQueryOptions({
    queryKey: qk.presets({ ...f, scope: 'structure' }),
    queryFn: ({ pageParam }): Promise<PresetPage> =>
      unwrap(
        api.GET('/admin/presets', {
          params: { query: { ...f, favorites: f.favorites || undefined, search: f.search || undefined, sort: 'position', page: pageParam, size: GALLERY_PAGE } },
        }),
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });

/** A gallery preset as a pinned version (its latest version; for a PM the latest published one). */
export function pinOf(p: PresetOut): PinnedPreset | null {
  if (!p.latest_version_id || p.version === null) return null;
  return { preset_version_id: p.latest_version_id, preset_id: p.id, version: p.version, name: p.name, layout: p.layout, color_mode: p.color_mode, mirrored: p.mirrored, status: p.status };
}

/**
 * PATCH /admin/websites/{id}: the Structure tab's last mode, the variant count (1–3) and the site type
 * (E-commerce / Landing only; 422 `website_type_locked` once a design is approved).
 */
export async function patchWebsite(
  qc: QueryClient,
  siteId: string,
  body: { structure_mode?: StructureMode; variant_count?: number; type?: Website['type'] },
): Promise<Website> {
  // Optimistic: the switches move at once; the answer replaces the cache, a refusal restores it.
  const before = qc.getQueryData<Website>(qk.website(siteId));
  qc.setQueryData<Website>(qk.website(siteId), (w) => (w ? { ...w, ...body } : w));
  try {
    const out = await unwrap(api.PATCH('/admin/websites/{website_id}', { params: { path: { website_id: siteId } }, body }));
    qc.setQueryData<Website>(qk.website(siteId), (w) => (w ? { ...w, ...out } : (out as Website)));
    return out as Website;
  } catch (e) {
    if (before) qc.setQueryData<Website>(qk.website(siteId), (w) => (w ? { ...w, ...Object.fromEntries(Object.keys(body).map((k) => [k, before[k as keyof Website]])) } : w));
    throw e;
  }
}

// ── AI mode (P4-A02 structure/router.py, suggest.py) ──

export type SuggestIn = Schemas['SuggestIn'];

/**
 * One proposed section (the `ai.suggest_structure` job's `result.sections`): its type, the chosen
 * version (null = the AI picks while generating), why, and what the cards draw for it.
 */
export type SuggestedSection = {
  section_type: string;
  preset_version_id: string | null;
  reason: string;
  design: { name: string; layout: string; color_mode: PinnedPreset['color_mode']; mirrored: boolean } | null;
};

/** POST /admin/websites/{id}/structure/suggest: 202 with the job. */
export async function suggestStructure(siteId: string, body: SuggestIn): Promise<string> {
  const out = await unwrap(api.POST('/admin/websites/{website_id}/structure/suggest', { params: { path: { website_id: siteId } }, body }));
  return out.job_id;
}

/** Runs AI mode: starts the suggestion and returns its proposal once the job is done. */
export async function runSuggestion(siteId: string, body: SuggestIn): Promise<SuggestedSection[]> {
  const result = await waitForJob(await suggestStructure(siteId, body));
  return (result.sections as SuggestedSection[] | undefined) ?? [];
}
